/**
 * useAuthGuard Hook - Prevents API calls before authentication is ready
 * 
 * SOLVES RACE CONDITION ISSUES:
 * 1. API calls made before auth tokens are available (causing 401 errors)
 * 2. Components loading data before user permissions are verified
 * 3. Inconsistent loading states across the application
 * 
 * USAGE:
 * const { isReady, user, token } = useAuthGuard();
 * 
 * // Only make API calls when isReady is true
 * useEffect(() => {
 *   if (isReady) {
 *     loadData();
 *   }
 * }, [isReady]);
 */

import { useMSALAuth } from '../contexts/MSALAuthContext';

export interface AuthGuardState {
  isReady: boolean;          // True when auth is complete and safe to make API calls
  isAuthenticated: boolean;  // True when user is logged in
  isTeamsAdmin: boolean;     // True when user has Teams Admin role
  isLoading: boolean;        // True when authentication is in progress
  user: any | null;          // User object with roles
  token: string | null;      // Access token for API calls
  error: any | null;         // Authentication error if any
}

/**
 * Guard hook that prevents components from making API calls
 * until authentication is fully initialized and tokens are available
 */
export const useAuthGuard = (): AuthGuardState => {
  const { 
    user, 
    token, 
    isLoading, 
    isAuthenticated, 
    isAuthReady, 
    error 
  } = useMSALAuth();

  // Authentication is ready when:
  // 1. Auth initialization is complete (isAuthReady = true)
  // 2. User is authenticated with valid tokens
  // 3. User has Teams Admin privileges (isTeamsAdmin = true)
  const isReady = isAuthReady && 
                 isAuthenticated && 
                 !!user && 
                 !!token && 
                 user.isTeamsAdmin === true;

  return {
    isReady,
    isAuthenticated,
    isTeamsAdmin: user?.isTeamsAdmin === true,
    isLoading,
    user,
    token,
    error
  };
};

/**
 * Hook variant for components that need to wait for auth but don't require Teams Admin
 * Useful for public or less privileged components
 */
export const useAuthGuardBasic = (): AuthGuardState => {
  const { 
    user, 
    token, 
    isLoading, 
    isAuthenticated, 
    isAuthReady, 
    error 
  } = useMSALAuth();

  // Basic auth is ready when:
  // 1. Auth initialization is complete
  // 2. Either user is authenticated OR auth failed (so we know the state)
  const isReady = isAuthReady;

  return {
    isReady,
    isAuthenticated,
    isTeamsAdmin: user?.isTeamsAdmin === true,
    isLoading,
    user,
    token,
    error
  };
};